import { createApi } from "@reduxjs/toolkit/query/react";
import { authBaseQuery } from "./baseQuery";
import { clearAuthData } from "../utils/authUtils";
import { persistAuthSessionFromUser } from "../utils/socialAuth";

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  user: {
    access_token: string;
    id: string;
    email: string;
    name: string;
  };
}

export interface AuthState {
  isAuthenticated: boolean;
  user: LoginResponse["user"] | null;
  token: string | null;
}

export const authApi = createApi({
  reducerPath: "authApi",
  baseQuery: authBaseQuery,
  tagTypes: ["Auth"],
  endpoints: (builder) => ({
    login: builder.mutation<LoginResponse, LoginRequest>({
      query: (credentials) => ({
        url: "/login",
        method: "POST",
        body: credentials,
      }),
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          persistAuthSessionFromUser(data.user);
        } catch (error: any) {
          // Check if it's a 401 error specifically
          if (error?.error?.status === 401) {
            // The base query will have already cleared the auth data
            console.log("Login failed: Invalid credentials or expired session");
          }
          // Re-throw so the component can handle it
          throw error;
        }
      },
      invalidatesTags: ["Auth"],
    }),
    logout: builder.mutation<void, void>({
      query: () => ({
        url: "/logout",
        method: "POST",
      }),
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error) {
          // Handle error, but still clear local storage
        } finally {
          // Clear local storage regardless of API call success
          clearAuthData();
        }
      },
      invalidatesTags: ["Auth"],
    }),
    getCurrentUser: builder.query<LoginResponse["user"], void>({
      query: () => "/get-auth-user",
      providesTags: ["Auth"],
    }),
  }),
});

export const { useLoginMutation, useLogoutMutation, useGetCurrentUserQuery } =
  authApi;
export const { useLazyGetCurrentUserQuery } = authApi;
